/**
 * Workspace Ticket Types
 *
 * Types for the workspace-based ticket model: the main WorkspaceTicket entity
 * and its subcollection entities (messages, attachments, participants, relations,
 * links, activity).
 */
// --- Ticket type and priority ---
export const TicketType = {
    BUG: 'BUG',
    IMPROVEMENT: 'IMPROVEMENT',
    REQUEST: 'REQUEST',
    QUESTION: 'QUESTION',
};
export const TicketPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL',
};
// --- Entity name constants ---
export const WORKSPACE_TICKET = 'WorkspaceTicket';
export const TICKET_MESSAGE = 'TicketMessage';
export const TICKET_ATTACHMENT = 'TicketAttachment';
export const TICKET_PARTICIPANT = 'TicketParticipant';
export const TICKET_RELATION = 'TicketRelation';
export const TICKET_LINK = 'TicketLink';
export const TICKET_ACTIVITY_LOG = 'TicketActivityLog';
// --- Lifecycle Status ---
/**
 * High-level lifecycle state of a ticket, independent of its workflow stage.
 *
 * A ticket in stage "Done" may still be OPEN (waiting requester acceptance).
 * A ticket may be ON_HOLD while remaining in its current stage.
 * Lifecycle status changes do not automatically move the stage.
 */
export const TicketLifecycleStatus = {
    OPEN: 'OPEN',
    ON_HOLD: 'ON_HOLD',
    CANCELLED: 'CANCELLED',
    CLOSED: 'CLOSED',
};
// --- Message Types ---
export const TicketMessageType = {
    /** Visible to all participants */
    COMMENT: 'COMMENT',
    /** Visible only to operators/admins */
    INTERNAL_NOTE: 'INTERNAL_NOTE',
    /** Generated by the system (stage change, assignment, automation, etc.) */
    SYSTEM_EVENT: 'SYSTEM_EVENT',
    /** Sent to request an explicit approval from a participant */
    APPROVAL_REQUEST: 'APPROVAL_REQUEST',
    /** Response to an approval request (approved / rejected) */
    APPROVAL_RESPONSE: 'APPROVAL_RESPONSE',
};
// --- Message / Attachment Visibility ---
export const TicketVisibility = {
    PUBLIC: 'PUBLIC',
    INTERNAL: 'INTERNAL',
    REQUESTER_ONLY: 'REQUESTER_ONLY',
};
// --- Participant Roles ---
export const TicketParticipantRole = {
    REQUESTER: 'REQUESTER',
    ASSIGNEE: 'ASSIGNEE',
    WATCHER: 'WATCHER',
    APPROVER: 'APPROVER',
    OPERATOR: 'OPERATOR',
};
// --- Relation Types ---
export const TicketRelationType = {
    /** One ticket is a sub-item of another */
    PARENT_CHILD: 'PARENT_CHILD',
    /** This ticket must be completed before the related one can start */
    DEPENDS_ON: 'DEPENDS_ON',
    /** This ticket blocks the related ticket from progressing */
    BLOCKS: 'BLOCKS',
    /** General association with no implied ordering */
    RELATED_TO: 'RELATED_TO',
    /** This ticket is a mirror of the related ticket (cross-org) */
    MIRRORS: 'MIRRORS',
};
// --- Cross-Organization Link Types ---
export const TicketLinkType = {
    /** Target ticket mirrors source ticket across organizations */
    MIRROR: 'MIRROR',
    /** Directional flow: source is upstream, target is downstream */
    UPSTREAM_DOWNSTREAM: 'UPSTREAM_DOWNSTREAM',
};
export const TicketLinkSyncPolicy = {
    /** No automatic synchronization; must be triggered manually */
    MANUAL: 'MANUAL',
    /** Stage changes are synchronized automatically */
    AUTO_STAGE: 'AUTO_STAGE',
    /** Stage and structured field changes are synchronized automatically */
    AUTO_FULL: 'AUTO_FULL',
};
// --- Activity Event Types ---
export const TicketActivityEventType = {
    CREATED: 'CREATED',
    STAGE_CHANGED: 'STAGE_CHANGED',
    LIFECYCLE_STATUS_CHANGED: 'LIFECYCLE_STATUS_CHANGED',
    ASSIGNEE_CHANGED: 'ASSIGNEE_CHANGED',
    FIELDS_UPDATED: 'FIELDS_UPDATED',
    MESSAGE_ADDED: 'MESSAGE_ADDED',
    ATTACHMENT_ADDED: 'ATTACHMENT_ADDED',
    ATTACHMENT_REMOVED: 'ATTACHMENT_REMOVED',
    PARTICIPANT_ADDED: 'PARTICIPANT_ADDED',
    PARTICIPANT_REMOVED: 'PARTICIPANT_REMOVED',
    RELATION_ADDED: 'RELATION_ADDED',
    AUTOMATION_EXECUTED: 'AUTOMATION_EXECUTED',
};
//# sourceMappingURL=workspace-ticket.js.map